/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IProductRefinerHierarchy } from '@msdyn365-commerce/commerce-entities';
import { AttributeDataType, DisplayTemplate, ProductRefinerSource, ProductRefinerValue } from '@msdyn365-commerce/retail-proxy';
import { getPayloadObject, getTelemetryAttributes, IPayLoad } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { ProductListInventoryFilteringOptions } from '../actions';
import { IChoiceSummaryProps } from './choice-summary.props';
import { isBoolean, getIntegerRangeName, isMatchingRefinementCriterion, isRangeType } from './utilities';
import { IProductRefinerHierarchyExt } from '../../../shared-utils/types';
import { decodeBase64String } from '../../../shared-utils/base64';
import { formatCurrencyNOK } from '../../../shared-utils/common-utils';

interface IRefinerMap {
    key: string,
    parent: string
    value: ProductRefinerValue;
    range?: boolean;
    rangeDisplayType?: number | undefined;
    hierarchy?: IProductRefinerHierarchyExt;
    isCategoryRefiner?: boolean;
}
interface IRefinerKey {
    key: string;
    parent: string;
    range?: boolean;
}

/**
 * ChoiceSummary component.
 */
@observer
export default class ChoiceSummary extends React.PureComponent<IChoiceSummaryProps> {
    private readonly closeButtonGlyph: string = 'msi-close-btn';

    private readonly payLoad: IPayLoad;

    @computed get selectedRefinersMap(): IRefinerMap[] {
        let { selectedChoices } = this.props;
        const { category, channelInventoryConfigurationId, refinerHierarchy } = this.props;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app configs are of generic type
        if (this.props.context?.app.config.productListInventoryDisplay === ProductListInventoryFilteringOptions.HideOOS) {
            selectedChoices = selectedChoices.filter(choice => {
                const parent = refinerHierarchy?.find(
                    (hierarchy: IProductRefinerHierarchy) =>
                        !!hierarchy.Values.find((value: ProductRefinerValue) => isMatchingRefinementCriterion(value, choice))
                );
                return parent && parent.RecordId !== channelInventoryConfigurationId;
            });
        }
        const result = selectedChoices.map((selectedRefiner: ProductRefinerValue, index) => {
            const RefinerValue = this._getKeyForRefinerValue(selectedRefiner, index.toString());
            const rangeParentDisplay = refinerHierarchy.filter(p => p.RecordId === selectedRefiner.RefinerRecordId);
            return {
                key: RefinerValue.key,
                parent: selectedRefiner.RefinerSourceValue === ProductRefinerSource.Category ? '' : RefinerValue.parent,
                value: selectedRefiner,
                range: RefinerValue.range,
                // parameter to store Display Template type
                rangeDisplayType: rangeParentDisplay[0]?.DisplayTemplateValue || undefined,
                hierarchy: rangeParentDisplay[0]
            } as IRefinerMap;
        });

        if (category?.ParentCategory) {
            result.unshift({
                key: category.Name || '',
                parent: '',
                value: {
                    DataTypeValue: AttributeDataType.None,
                    LeftValueBoundString: category.Name,
                    LeftValueBoundLocalizedString: category.Name,
                    RefinerRecordId: 0,
                    UnitText: ''
                },
                isCategoryRefiner: true
            });
        }

        return result;
    }

    constructor(props: Readonly<IChoiceSummaryProps>) {
        super(props);
        this.payLoad = getPayloadObject('click', this.props.telemetryContent!, '');
    }

    public render(): JSX.Element {
        const { clearAllText, label, classNames, choiceAriaLabel, closeAriaLabel,yesLabel,noLabel } = this.props;
        const items = this.selectedRefinersMap;
        this.payLoad.contentAction.etext = clearAllText;
        const clearAllAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);

        return (
            <div className='msc-choice-summary'>
                <div className='msc-choice-summary__header'>
                    <span className='msc-choice-summary__label'>{label}</span>
                    <a
                        href={this.props.urlBuilder({}, true)}
                        className={classnames('msc-choice-summary__clear-all', { hide: items.length === 0 })}
                        {...clearAllAttributes}
                        onClick={this._onClick}
                        aria-disabled={items.length === 0}
                    >
                        {clearAllText}
                    </a>
                </div>
                <ul className={classnames(classNames, 'msc-choice-summary__list', 'list-unstyled')}>
                    {items.length === 0 && (
                        <li className='msc-choice-summary__list-item none' key={'none'}>
                            <a
                                className='msc-choice-summary__item'
                                href='#'
                                aria-label={`None`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                role='button'
                            >
                                <span>
                                    Ingen
                                </span>
                                <span
                                    className={`${this.closeButtonGlyph} msc-choice-summary__glyph`}
                                    role='button'
                                    aria-label={closeAriaLabel}
                                />
                            </a>
                        </li>
                    )}
                    {items.map((item: IRefinerMap) => {
                        this.payLoad.contentAction.etext = item.key;
                        const attribute = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
                        let LeftValueBoundString = item.value.LeftValueBoundString;
                        if (item.value && isBoolean(item.value.DataTypeValue)) {
                            LeftValueBoundString = LeftValueBoundString === '1' ? yesLabel : noLabel;
                        } else if (Number(this.props.transitRefinerRecordId) === item.value.RefinerRecordId) {
                            LeftValueBoundString = LeftValueBoundString === '1' ? yesLabel : noLabel;
                        } else if (item.parent === 'Sperret for Svalbard') {
                            LeftValueBoundString = LeftValueBoundString === '1' ? yesLabel : noLabel;
                        }

                        // Get range value for Range display and integer type only (not for Integer with TextBox(4) display)
                        const rangeInteger =
                            item.hierarchy?.RangeToPredefinedRanges ||
                            item.value.DataTypeValue === AttributeDataType.Integer &&
                            item.rangeDisplayType &&
                            item.rangeDisplayType === DisplayTemplate.Range &&
                            item.value.RefinerRecordId !== 0;
                        let newIntRange = rangeInteger
                            ? item.key
                            : LeftValueBoundString && item.value.LeftValueBoundLocalizedString === ''
                            ? LeftValueBoundString
                            : (item.value.LeftValueBoundLocalizedString || item.value.LeftValueBoundLocalizedString === undefined) &&
                              item.value.RefinerRecordId === 0
                            ? item.key
                            : item.value.LeftValueBoundLocalizedString;

                        let labelText = item.parent;

                        if (labelText?.startsWith('{base64}:') && newIntRange) {
                            labelText = labelText.replace('{base64}:', '');
                            newIntRange = decodeBase64String(newIntRange);
                        }

                        return (
                            <li className='msc-choice-summary__list-item' key={item.key}>
                                <a
                                    className='msc-choice-summary__item'
                                    href={this.props.urlBuilder(item.value, false)}
                                    aria-label={`${LeftValueBoundString} ${choiceAriaLabel}`}
                                    onClick={this._onClick}
                                    role='button'
                                    id={item.key}
                                    {...attribute}
                                >
                                    <span>
                                        {item?.parent && <span style={{ fontWeight: 'bold' }}>{labelText}:</span>}
                                        {newIntRange}
                                    </span>
                                    <span
                                        className={`${this.closeButtonGlyph} msc-choice-summary__glyph`}
                                        role='button'
                                        aria-label={closeAriaLabel}
                                    />
                                </a>
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    }

    private _getKeyForRefinerValue(productRefinerValue: ProductRefinerValue, index: string): IRefinerKey {
        const { choiceFormat, choiceRangeValueFormat, refinerHierarchy, telemetry, refinerValues } = this.props;
        const overallFormat = choiceFormat || '{1}';
        const rangeFormat = choiceRangeValueFormat;
        let refinerName = '';
        let parent: IProductRefinerHierarchy | undefined;
        if (refinerHierarchy && refinerHierarchy.find) {
            parent = refinerHierarchy.find(
                (hierarchy: IProductRefinerHierarchy) =>
                    !!hierarchy.Values.find((value: ProductRefinerValue) => isMatchingRefinementCriterion(value, productRefinerValue))
            );

            if (!parent) {
                telemetry.warning('[choice-summary] could not find parent of selected refiner value');
            } else {
                refinerName = parent.KeyName || '';
            }
        }
        let refinerValueName: string;
        if (isRangeType(productRefinerValue.DataTypeValue)) {
            refinerValueName = rangeFormat
                .replace('{0}', this._formatPrice(productRefinerValue.LeftValueBoundString, productRefinerValue.UnitText))
                .replace('{1}', this._formatPrice(productRefinerValue.RightValueBoundString, productRefinerValue.UnitText));
        } else if (productRefinerValue.RefinerRecordId === 0) {
            const filterValue = refinerValues?.Values.find(
                refiner => refiner.LeftValueBoundString === productRefinerValue.LeftValueBoundString
            );
            refinerValueName = (filterValue ? filterValue.LeftValueBoundLocalizedString : productRefinerValue.LeftValueBoundString) || '';
        } else {
            refinerValueName = productRefinerValue.LeftValueBoundLocalizedString || productRefinerValue.LeftValueBoundString || '';
        }
        const rangeRefiners = [
            'Rating',
            'Price',
            'Pris'
        ];
        if (rangeRefiners.includes(refinerName)) {
            return {
                key: overallFormat.replace('{0}', refinerName).replace('{1}', `${refinerValueName}`),
                parent: refinerName,
                range: true
            };
        }
        // Adding integer range format
        if (
            productRefinerValue.DataTypeValue === AttributeDataType.Integer &&
            parent?.DisplayTemplateValue === DisplayTemplate.Range &&
            productRefinerValue.RefinerRecordId !== 0
        ) {
            refinerValueName = getIntegerRangeName(productRefinerValue);
            return {
                key: refinerValueName,
                parent: refinerName
            };
        }
        return {
            key: overallFormat.replace('{0}', refinerName).replace('{1}', `${refinerValueName}_${refinerName}_${index}`),
            parent: refinerName
        };
    }

    private _formatPrice(amount: string | undefined, currency: string | undefined): string {
        if (!amount || !currency) {
            this.props.telemetry.trace('[choice-summary] could not format price');
            return amount || '';
        }
        let result = amount;

        try {
            result = formatCurrencyNOK(Number(amount), this.props.context, currency);
        } catch (error) {
            this.props.telemetry.warning(`Failed to format price for ${result}: ${error}`);
        }

        return result;
    }

    private readonly _onClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();
        e.stopPropagation();

        if (this.selectedRefinersMap.length === 0) {
            return;
        }

        const target = e.currentTarget as HTMLElement;
        const clearAll = target.getAttribute('class')!.includes('choice-summary__clear-all');
        const selectedRefiner = clearAll ? undefined : this._getSelectedRefinerChoice(target);

        if (this.props.onChoiceClicked) {
            this.props.onChoiceClicked({
                clearAll,
                itemClicked: target,
                choiceClicked: selectedRefiner,
                nextItemToFocus: target.nextSibling as HTMLElement,
                clearCategory: !!this.selectedRefinersMap.find(selected => target.id === selected.key)?.isCategoryRefiner
            });
        }
    };

    private _getSelectedRefinerChoice(itemClicked: HTMLElement): ProductRefinerValue | undefined {
        const result = this.selectedRefinersMap.find(selected => itemClicked.id === selected.key);
        return (result && result.value) || undefined;
    }
}
